/**
 * Assassin Workshop — Airsoft Configurator local bootstrap
 * Lot 8 / v1.2.1
 * Lightweight analytics + relaxed compatibility safety + lazy full engine/attribution.
 */
(() => {
  'use strict';

  const bootstrapScript = document.currentScript;
  const roots = Array.from(document.querySelectorAll('[data-aw-configurator]'));
  if (!roots.length || window.AWConfiguratorBootstrap?.version) return;

  const eventKey = (value = '') => String(value).trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const ensureAnalytics = () => {
    if (window.AWAnalytics?.track) return;
    const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
    const preview = Boolean(window.Shopify?.designMode || (themeRole && themeRole !== 'main') || /(?:\?|&)preview_theme_id=/.test(location.search) || /(?:\?|&)previewPath=/.test(location.search));
    const safe = (value) => {
      if (value === null || value === undefined) return undefined;
      if (typeof value === 'boolean' || typeof value === 'number') return value;
      if (typeof value === 'string') return value.slice(0, 180);
      if (Array.isArray(value)) return value.slice(0, 12).map(safe).filter((item) => item !== undefined);
      return undefined;
    };
    const track = (name, payload = {}) => {
      const event = eventKey(name);
      if (!event) return;
      const extra = Object.entries(payload).reduce((result, [key, value]) => {
        const cleanKey = eventKey(key).slice(0, 60);
        const cleanValue = safe(value);
        if (cleanKey && cleanValue !== undefined) result[cleanKey] = cleanValue;
        return result;
      }, {});
      const data = { schema_version: 1, path: location.pathname.slice(0, 180), locale: document.documentElement.lang || '', currency: window.Shopify?.currency?.active || '', preview_mode: preview, ...extra };
      const fullName = `assassin_workshop:${event}`;
      window.dispatchEvent(new CustomEvent('aw:analytics', { detail: { event: fullName, payload: data, suppressed: preview } }));
      if (preview) return;
      const publish = (attempt = 0) => {
        try {
          if (window.Shopify?.analytics?.publish) { window.Shopify.analytics.publish(fullName, data); return; }
        } catch (_) { return; }
        if (attempt < 8) setTimeout(() => publish(attempt + 1), 250);
      };
      publish();
      try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: `aw_${event}`, ...data }); } catch (_) {}
    };
    window.AWAnalytics = { version: 'configurator-lite-1.0.0', track, preview };
  };

  ensureAnalytics();
  const track = (name, payload = {}) => { try { window.AWAnalytics?.track?.(name, payload); } catch (_) {} };
  const readJson = (key, fallback = {}) => {
    try { const value = JSON.parse(localStorage.getItem(key) || 'null'); return value ?? fallback; } catch (_) { return fallback; }
  };

  const configuratorState = (root) => {
    const state = readJson(root.dataset.storageKey || 'aw_airsoft_configurator', {});
    const price = root.querySelector('[data-aw-config-total]')?.textContent || '';
    const normalizedPrice = String(price).replace(/[^0-9,.-]/g, '').replace(/\.(?=.*\.)/g, '').replace(',', '.');
    const numeric = Number(normalizedPrice);
    return {
      platform: state.platform || '', manufacturer: state.manufacturer || '', replica_type: state.type || '', goal: state.goal || '',
      budget_cents: Number(state.budget || 0), step: Number(state.step || 1),
      selected_count: Array.isArray(state.selected) ? state.selected.length : 0,
      total_cents: Number.isFinite(numeric) ? Math.round(numeric * 100) : 0
    };
  };

  let pendingConfiguration = null;
  roots.forEach((root) => {
    setTimeout(() => {
      const state = configuratorState(root);
      track('configurator_viewed', { restored_step: state.step, has_restored_platform: Boolean(state.platform), restored_selected_count: state.selected_count });
    }, 0);
    let lastCompletedHash = '';
    const after = (callback) => setTimeout(callback, 0);
    root.addEventListener('click', (event) => {
      const platform = event.target.closest?.('[data-aw-config-platform]');
      if (platform) after(() => track('configurator_platform_selected', configuratorState(root)));
      const next = event.target.closest?.('[data-aw-config-next]');
      const back = event.target.closest?.('[data-aw-config-back]');
      const nav = event.target.closest?.('[data-aw-step-nav]');
      if (next || back || nav) after(() => {
        const state = configuratorState(root);
        track('configurator_step_viewed', state);
        if (state.step === 4) {
          const hash = `${state.platform}|${state.manufacturer}|${state.replica_type}|${state.goal}|${state.selected_count}`;
          if (hash !== lastCompletedHash) { lastCompletedHash = hash; track('configuration_completed', state); }
        }
      });
      const toggle = event.target.closest?.('[data-aw-config-toggle]');
      if (toggle) {
        const wasSelected = toggle.getAttribute('aria-pressed') === 'true' || /retirer/i.test(toggle.textContent || '');
        const variantId = toggle.dataset.awConfigToggle || '';
        const title = toggle.closest('[data-aw-rendered-product]')?.querySelector('h3')?.textContent?.trim() || '';
        after(() => track(wasSelected ? 'configurator_product_removed' : 'configurator_product_selected', { ...configuratorState(root), variant_id: variantId, product_title: title }));
      }
      if (event.target.closest?.('[data-aw-config-add-all]')) after(() => {
        pendingConfiguration = { ...configuratorState(root), time: Date.now() };
        track('configuration_add_to_cart_intent', pendingConfiguration);
      });
      if (event.target.closest?.('[data-aw-save-confirm]')) after(() => track('configuration_saved', configuratorState(root)));
      if (event.target.closest?.('[data-aw-config-share]')) after(() => track('configuration_share_started', configuratorState(root)));
      if (event.target.closest?.('[data-aw-config-compare]')) after(() => track('configuration_compare_opened', configuratorState(root)));
      if (event.target.closest?.('[data-aw-config-reset]')) after(() => track('configuration_reset', { source: 'configurator' }));
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches?.('[data-aw-config-manufacturer],[data-aw-config-type],[data-aw-config-goal]')) after(() => track('configurator_filters_changed', configuratorState(root)));
    });
  });
  document.addEventListener('cart:updated', () => {
    if (pendingConfiguration && Date.now() - pendingConfiguration.time < 15000) {
      track('configuration_add_to_cart', pendingConfiguration);
      pendingConfiguration = null;
    }
  });
  document.addEventListener('click', (event) => {
    const node = event.target?.closest?.('[data-aw-p1-track]');
    if (node) track(node.dataset.awP1Track || 'p1_click', { cluster: node.dataset.awP1Cluster || '', href: node.getAttribute('href') || '', label: (node.textContent || '').trim().slice(0, 120) });
  }, true);

  const norm = (value = '') => String(value).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const split = (value = '') => String(value).split(',').map(norm).filter(Boolean);
  const technicalScore = (card) => {
    const root = card?.closest?.('[data-aw-configurator]');
    const variant = card?.dataset.awRenderedProduct;
    if (!root || !variant) return -1;
    let state = {};
    try { state = JSON.parse(localStorage.getItem(root.dataset.storageKey || 'aw_airsoft_configurator') || '{}') || {}; } catch (_) {}
    const source = Array.from(root.querySelectorAll('[data-aw-config-product]')).find((node) => String(node.dataset.variantId) === String(variant));
    if (!source || !state.platform) return -1;
    const platforms = split(source.dataset.platforms), manufacturers = split(source.dataset.manufacturers), types = split(source.dataset.types);
    const platform = norm(state.platform), manufacturer = norm(state.manufacturer), type = norm(state.type);
    if (!platforms.includes(platform)) return -1;
    let score = 100;
    if (manufacturer) score += manufacturers.includes(manufacturer) ? 30 : manufacturers.length ? -20 : -8;
    if (type) score += types.includes(type) ? 25 : types.length ? -20 : -8;
    return score;
  };
  const secureCard = (card) => {
    const badge = card?.querySelector?.('.aw-configurator__product-badge');
    if (!badge) return;
    const score = technicalScore(card);
    const label = score >= 145 ? 'Compatibilité forte' : score >= 120 ? 'Compatible sous réserve' : score >= 0 ? 'Correspondance plateforme — à vérifier' : 'À vérifier';
    if (badge.textContent.trim() !== label) badge.textContent = label;
    const needsVerification = score < 0;
    const toggle = card.querySelector('[data-aw-config-toggle]');
    if (toggle) {
      toggle.disabled = needsVerification;
      toggle.setAttribute('aria-disabled', needsVerification ? 'true' : 'false');
      toggle.title = needsVerification ? 'Sélectionnez d’abord une plateforme AW correspondant à ce produit.' : (score < 120 ? 'Sélection autorisée ; vérifiez le fabricant, le type et la version exacte avant commande.' : '');
    }
    card.dataset.awCompatibilityGuard = needsVerification ? 'blocked' : score < 120 ? 'review' : 'ready';
  };
  const improveWording = (root) => {
    root.querySelectorAll('.aw-configurator__product').forEach(secureCard);
    root.querySelectorAll('span,strong,p').forEach((node) => {
      const text = node.textContent.trim();
      if (text === 'Compatibilité vérifiée') node.textContent = 'Données AW référencées';
      else if (text === 'Analyse des produits compatibles…') node.textContent = 'Analyse des produits correspondant à la plateforme…';
      else if (/données de compatibilité vérifiées/i.test(text)) node.textContent = text.replace(/données de compatibilité vérifiées(?: par Assassin Workshop)?/i, 'données de compatibilité AW référencées');
    });
    const result = root.querySelector('[data-aw-config-results]');
    if (result) {
      const match = result.textContent.trim().match(/^(\d+)\s+produits?\s+compatibles?$/i);
      if (match) result.textContent = `${match[1]} résultat${match[1] === '1' ? '' : 's'} pour cette plateforme`;
    }
  };
  const invalidSelectedItems = (root) => {
    let state = {};
    try { state = JSON.parse(localStorage.getItem(root.dataset.storageKey || 'aw_airsoft_configurator') || '{}') || {}; } catch (_) {}
    const selected = new Set((state.selected || []).map(String));
    if (!selected.size) return [];
    return Array.from(root.querySelectorAll('[data-aw-config-product]')).filter((node) => selected.has(String(node.dataset.variantId))).filter((node) => {
      const fake = document.createElement('article');
      fake.dataset.awRenderedProduct = node.dataset.variantId;
      fake.hidden = true;
      root.append(fake);
      const score = technicalScore(fake);
      fake.remove();
      return score < 0;
    });
  };
  roots.forEach((root) => {
    improveWording(root);
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; improveWording(root); });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    root.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('[data-aw-config-toggle]');
      if (toggle) {
        secureCard(toggle.closest('.aw-configurator__product'));
        if (toggle.disabled || toggle.getAttribute('aria-disabled') === 'true') { event.preventDefault(); event.stopImmediatePropagation(); return; }
      }
      const addAll = event.target.closest?.('[data-aw-config-add-all]');
      if (addAll) {
        const invalid = invalidSelectedItems(root);
        if (invalid.length) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const notice = root.querySelector('[data-aw-config-notice]');
          if (notice) {
            notice.hidden = false;
            notice.textContent = `${invalid.length} composant${invalid.length > 1 ? 's' : ''} n’appartient plus à la plateforme sélectionnée et doit être retiré avant l’ajout au panier.`;
            notice.classList.add('is-error');
            notice.classList.remove('is-success');
          }
          root.querySelector('[data-aw-step="3"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }
      }
    }, true);
  });

  let loaded = false, loading = null, attributionLoading = null, replaying = false, failed = false;
  const siblingSrc = (filename) => {
    try { const url = new URL(bootstrapScript?.src || '', location.href); url.pathname = url.pathname.replace(/aw-airsoft-configurator(?:-cwv-20261002(?:-v[0-9]+)?)?\.js$/, filename); return url.href; } catch (_) { return ''; }
  };
  const coreSrc = siblingSrc('aw-airsoft-configurator-core-cwv-20261002-v4.js');
  const degrade = (root) => {
    if (!root) return;
    root.dataset.awConfiguratorDegraded = 'true';
    let notice = root.querySelector('[data-aw-config-runtime-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'aw-configurator__notice is-error';
      notice.dataset.awConfigRuntimeNotice = 'true';
      notice.setAttribute('role', 'alert');
      const hero = root.querySelector('.aw-configurator__hero');
      const host = hero?.parentElement || root;
      if (hero) hero.insertAdjacentElement('afterend', notice);
      else host.prepend(notice);
    }
    notice.hidden = false;
    notice.replaceChildren(
      document.createTextNode('Le configurateur n’a pas pu démarrer. Rechargez la page ou utilisez '),
      Object.assign(document.createElement('a'), { href: '/pages/compatibilite-airsoft', textContent: 'Compatibility AW' }),
      document.createTextNode(' / '),
      Object.assign(document.createElement('a'), { href: '/pages/mon-atelier-airsoft', textContent: 'Mon Atelier' }),
      document.createTextNode('.')
    );
  };
  const fail = (error) => {
    failed = true;
    loading = null;
    roots.forEach(degrade);
    return error instanceof Error ? error : new Error('Configurator core unavailable');
  };
  const ensureAttribution = () => {
    if (window.AWAttribution?.version) return Promise.resolve();
    if (attributionLoading) return attributionLoading;
    const src = siblingSrc('aw-attribution-v2.js');
    if (!src) return Promise.resolve();
    attributionLoading = new Promise((resolve) => {
      const node = document.createElement('script'); node.src = src; node.async = true; node.dataset.awLazyAttribution = 'configurator';
      node.addEventListener('load', resolve, { once: true }); node.addEventListener('error', resolve, { once: true }); document.head.appendChild(node);
    });
    return attributionLoading;
  };
  const initialiseLate = () => {
    if (document.readyState === 'loading') return;
    roots.forEach((root) => {
      if (root.dataset.awReady === 'true') return;
      const scope = root.closest('.shopify-section') || root.parentElement || document;
      scope.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { awLazyBootstrap: true } }));
    });
  };
  const load = () => {
    if (loaded || roots.every((root) => root.dataset.awReady === 'true')) { loaded = true; return Promise.resolve(); }
    if (failed) return Promise.reject(new Error('Configurator core unavailable'));
    if (loading) return loading;
    if (!coreSrc) return Promise.reject(fail(new Error('Configurator core URL unavailable')));
    ensureAttribution().catch(() => {});
    loading = new Promise((resolve, reject) => {
      const node = document.createElement('script'); node.src = coreSrc; node.async = true; node.fetchPriority = 'low'; node.dataset.awConfiguratorCore = 'true';
      node.addEventListener('load', () => { loaded = true; initialiseLate(); roots.forEach((root) => { root.dataset.awConfiguratorCoreLoaded = 'true'; improveWording(root); }); resolve(); }, { once: true });
      node.addEventListener('error', () => { reject(fail(new Error('Configurator core failed to load'))); }, { once: true });
      document.head.appendChild(node);
    });
    return loading;
  };
  const warm = () => { load().catch(() => {}); };
  roots.forEach((root) => {
    if (root.dataset.awConfiguratorBootstrap === 'true') return;
    root.dataset.awConfiguratorBootstrap = 'true';
    root.addEventListener('pointerover', warm, { once: true, passive: true });
    root.addEventListener('pointerdown', warm, { once: true, passive: true });
    root.addEventListener('touchstart', warm, { once: true, passive: true });
    root.addEventListener('focusin', warm, { once: true });
    root.addEventListener('click', (event) => {
      if (failed) { degrade(root); return; }
      if (loaded || replaying || event.defaultPrevented) return;
      const target = event.target?.closest?.('button,[role="button"]');
      if (!target || !root.contains(target)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      load().then(() => { replaying = true; target.click(); replaying = false; }).catch(() => { degrade(root); });
    }, true);
    root.addEventListener('input', (event) => {
      if (failed) { degrade(root); return; }
      if (loaded || replaying) return;
      const target = event.target;
      load().then(() => { if (!target?.dispatchEvent) return; replaying = true; target.dispatchEvent(new Event('input', { bubbles: true })); replaying = false; }).catch(() => { degrade(root); });
    }, { capture: true, once: true });
    root.addEventListener('change', (event) => {
      if (failed) { degrade(root); return; }
      if (loaded || replaying) return;
      const target = event.target;
      load().then(() => { if (!target?.dispatchEvent) return; replaying = true; target.dispatchEvent(new Event('change', { bubbles: true })); replaying = false; }).catch(() => { degrade(root); });
    }, { capture: true, once: true });
  });
  const hasStoredState = roots.some((root) => {
    const keys = [root.dataset.storageKey, root.dataset.savedKey, root.dataset.historyKey, root.dataset.platformKey, root.dataset.manufacturerKey, root.dataset.typeKey].filter(Boolean);
    try { return keys.some((key) => localStorage.getItem(key)); } catch (_) { return false; }
  });
  const hasSharedConfiguration = roots.some((root) => new URLSearchParams(location.search).has(root.dataset.shareParam || 'aw_config'));
  window.AWConfiguratorBootstrap = { version: '1.2.3-cwv-20261002', load, get loaded() { return loaded; }, get failed() { return failed; }, policy: 'platform-match-selectable;lazy-core-visible-degraded-state' };
  if (hasStoredState || hasSharedConfiguration || window.Shopify?.designMode) warm();
  else {
    const prepare = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 1500 });
      else setTimeout(warm, 250);
    };
    if (document.readyState === 'complete') prepare();
    else window.addEventListener('load', prepare, { once: true });
  }
})();

